import { useMemo, useState } from "react";
import { FONT, COLOR, RADIUS } from "../lib/tokens";
import { useCommunityLeaderboard } from "../lib/useCommunityRecipes";
import { findRecipe } from "../data/recipes";
import { useUserRecipes } from "../lib/useUserRecipes";
import { absoluteAvatarURL } from "../lib/absoluteAvatarURL";

// Community — recipe leaderboard tab.
//
// Ranks community-published recipes by a volume+rating score
// (community_leaderboard view). Every row answers the three questions
// the old UI hid: WHO made it (author avatar + name), HOW MANY TIMES
// it's been cooked (cook count, in-window), and WHY it ranks (rating +
// a plain-language explanation). Tap a row to cook it — your rating
// after cooking moves it up.
//
// Self-contained dark cards with light text so the leaderboard reads
// the same regardless of the shell theme behind it (the old frosted
// COLOR.deep cards washed every label out to near-invisible).
//
// Props:
//   userId — viewer
//   onCook(recipe, opts) — opens CookMode with community attribution.

const WINDOWS = [
  { id: "alltime", label: "All-time" },
  { id: "monthly", label: "Monthly"  },
  { id: "weekly",  label: "Weekly"   },
];

// Card palette — solid, high-contrast on any shell.
const CARD       = "#221D16";
const CARD_TOP3  = "#2A2316";
const INK        = "#F3ECDD";   // primary light text
const INK_DIM    = "#B6A98D";   // secondary
const INK_FAINT  = "#8C8068";   // tertiary
const HAIR       = "rgba(255,255,255,0.08)";
const GOLD_HAIR  = "rgba(212,166,55,0.40)";

function ratingWord(avg) {
  if (!(avg > 0)) return null;
  if (avg >= 2.5) return "Nailed it";
  if (avg >= 1.5) return "Crowd-pleaser";
  if (avg >= 0.75) return "Mixed reviews";
  return "Rough";
}

function sinceLabel(iso) {
  if (!iso) return "";
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (days <= 0) return "shared today";
  if (days === 1) return "shared yesterday";
  if (days < 30) return `shared ${days}d ago`;
  const mo = Math.floor(days / 30);
  return `shared ${mo}mo ago`;
}

// Five-star display from the internal 0..3 rating. avg/3 → 0..5,
// rounded to the nearest half so a 2.1/3 reads as a sensible 3.5★.
function Stars({ avg, size = 13 }) {
  const five = Math.max(0, Math.min(5, (Number(avg) / 3) * 5));
  const rounded = Math.round(five * 2) / 2;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", letterSpacing: "0.5px", fontSize: size, lineHeight: 1 }} aria-label={`${five.toFixed(1)} out of 5 stars`}>
      {[0, 1, 2, 3, 4].map((i) => {
        const fill = rounded - i;
        const ch = fill >= 1 ? "★" : fill >= 0.5 ? "★" : "☆";
        const color = fill >= 0.5 ? COLOR.gold : "rgba(212,166,55,0.35)";
        return <span key={i} style={{ color, opacity: fill >= 0.5 && fill < 1 ? 0.6 : 1 }}>{ch}</span>;
      })}
    </span>
  );
}

export default function Community({ userId, onCook }) {
  const [window, setWindow] = useState("alltime");
  const [openDiagId, setOpenDiagId] = useState(null);
  const { rows, loading, refresh } = useCommunityLeaderboard({ window });
  const { findBySlug } = useUserRecipes(userId);
  const [query, setQuery] = useState("");
  const [cuisineFilter, setCuisineFilter] = useState(null);

  // Cuisines present on the board, for the discovery filter chips.
  const cuisines = useMemo(() => {
    const s = new Set();
    for (const r of rows) if (r.cuisine) s.add(r.cuisine);
    return Array.from(s).sort();
  }, [rows]);

  // Search + cuisine filter over the loaded board, keeping each
  // recipe's TRUE leaderboard rank (its index in the full sorted list)
  // so a search hit still shows "#7", not a re-numbered position.
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows
      .map((row, i) => ({ row, rank: i + 1 }))
      .filter(({ row }) => {
        if (cuisineFilter && row.cuisine !== cuisineFilter) return false;
        if (!q) return true;
        return (row.title || "").toLowerCase().includes(q)
          || (row.author_name || "").toLowerCase().includes(q)
          || (row.cuisine || "").toLowerCase().includes(q);
      });
  }, [rows, query, cuisineFilter]);

  const handleCook = (row) => {
    const recipe = findRecipe(row.slug, findBySlug, row.author_id);
    const attribution = {
      _communityRecipeId: row.recipe_id,
      _communityAuthorId: row.author_id,
    };
    if (!recipe) {
      onCook?.({ slug: row.slug, title: row.title, emoji: row.emoji, ...attribution });
      return;
    }
    onCook?.({ ...recipe, ...attribution });
  };

  return (
    <div style={{ padding: "20px 14px 90px", maxWidth: 640, margin: "0 auto" }}>
      <div style={{
        fontFamily: FONT.mono, fontSize: 12, fontWeight: 700,
        color: COLOR.gold, letterSpacing: "0.18em",
        textAlign: "center", marginBottom: 6, textTransform: "uppercase",
      }}>
        Community
      </div>
      <div style={{
        fontFamily: FONT.itemTitle, fontStyle: "italic",
        fontSize: 30, fontWeight: 500, color: COLOR.ink,
        textAlign: "center", marginBottom: 6, lineHeight: 1.05,
      }}>
        The leaderboard
      </div>
      <div style={{
        fontFamily: FONT.sans, fontSize: 13, color: COLOR.dim,
        textAlign: "center", marginBottom: 18, lineHeight: 1.45, padding: "0 16px",
      }}>
        Ranked by how many people cooked each recipe and how they rated it.
        Cook one and your rating moves it up.
      </div>

      {/* Window toggle */}
      <div style={{ display: "flex", gap: 6, justifyContent: "center", marginBottom: 20 }}>
        {WINDOWS.map((w) => {
          const active = w.id === window;
          return (
            <button
              key={w.id}
              type="button"
              onClick={() => setWindow(w.id)}
              className="mcm-focusable"
              style={{
                padding: "8px 16px",
                background: active ? COLOR.gold : "rgba(255,255,255,0.06)",
                border: `1px solid ${active ? COLOR.gold : HAIR}`,
                borderRadius: RADIUS.pill,
                fontFamily: FONT.mono, fontSize: 12, fontWeight: 700,
                letterSpacing: "0.08em", textTransform: "uppercase",
                color: active ? "#2A2316" : INK_DIM,
                cursor: "pointer", touchAction: "manipulation",
              }}
            >
              {w.label}
            </button>
          );
        })}
      </div>

      {/* Search + discover */}
      {rows.length > 0 && (
        <>
          <div style={{ position: "relative", marginBottom: 12 }}>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search recipes, people, cuisines…"
              style={{
                width: "100%", boxSizing: "border-box",
                padding: "11px 36px 11px 14px",
                background: "rgba(255,255,255,0.06)",
                border: `1px solid ${HAIR}`, borderRadius: 12,
                fontFamily: FONT.sans, fontSize: 14, color: INK,
                outline: "none",
              }}
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery("")}
                aria-label="Clear search"
                style={{
                  position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)",
                  width: 24, height: 24, borderRadius: 12, border: "none",
                  background: "rgba(255,255,255,0.1)", color: INK_DIM,
                  fontSize: 14, cursor: "pointer", touchAction: "manipulation",
                }}
              >
                ×
              </button>
            )}
          </div>
          {cuisines.length > 1 && (
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 18 }}>
              {cuisines.map((c) => {
                const active = cuisineFilter === c;
                return (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setCuisineFilter(active ? null : c)}
                    className="mcm-focusable"
                    style={{
                      padding: "5px 12px", borderRadius: 999,
                      background: active ? "rgba(212,166,55,0.18)" : "rgba(255,255,255,0.05)",
                      border: `1px solid ${active ? GOLD_HAIR : HAIR}`,
                      color: active ? COLOR.gold : INK_DIM,
                      fontFamily: FONT.mono, fontSize: 12, fontWeight: 600,
                      letterSpacing: "0.06em", textTransform: "capitalize",
                      cursor: "pointer", touchAction: "manipulation",
                    }}
                  >
                    {c}
                  </button>
                );
              })}
            </div>
          )}
        </>
      )}

      {loading && rows.length === 0 && (
        <div style={{ fontFamily: FONT.sans, fontSize: 13, color: COLOR.sub, textAlign: "center", padding: "32px 16px" }}>
          loading…
        </div>
      )}
      {!loading && rows.length > 0 && visible.length === 0 && (
        <div style={{ fontFamily: FONT.sans, fontSize: 14, color: INK_DIM, textAlign: "center", padding: "32px 16px" }}>
          No matches{query ? ` for “${query}”` : ""}{cuisineFilter ? ` in ${cuisineFilter}` : ""}.
        </div>
      )}
      {!loading && rows.length === 0 && (
        <div style={{
          fontFamily: FONT.sans, fontSize: 14, color: COLOR.sub,
          textAlign: "center", padding: "40px 24px", lineHeight: 1.5,
          border: `1px dashed ${COLOR.edge}`, borderRadius: 14,
        }}>
          No published recipes yet. Open one of your recipes and tap
          "Publish to Community" to start the board.
        </div>
      )}

      {/* Ranked list */}
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {visible.map(({ row, rank }) => {
          const top3 = rank <= 3;
          const cooks = window === "weekly"
            ? Number(row.cook_count_weekly) || 0
            : window === "monthly"
              ? Number(row.cook_count_monthly) || 0
              : Number(row.cook_count) || 0;
          const cookers = window === "weekly"
            ? Number(row.distinct_cookers_weekly) || 0
            : window === "monthly"
              ? Number(row.distinct_cookers_monthly) || 0
              : Number(row.distinct_cookers) || 0;
          const allTime = Number(row.cook_count) || 0;
          const allCookers = Number(row.distinct_cookers) || 0;
          const avg = Number(row.avg_rating) || 0;
          const author = row.author_name || "Someone";
          const avatar = absoluteAvatarURL(row.author_avatar_url);
          const isDiagOpen = openDiagId === row.recipe_id;

          return (
            <div
              key={row.recipe_id}
              style={{
                background: top3 ? CARD_TOP3 : CARD,
                border: `1px solid ${top3 ? GOLD_HAIR : HAIR}`,
                borderRadius: 16,
                overflow: "hidden",
                boxShadow: top3 ? "0 0 0 1px rgba(212,166,55,0.12), 0 8px 24px rgba(0,0,0,0.35)" : "0 4px 16px rgba(0,0,0,0.28)",
              }}
            >
              <button
                type="button"
                onClick={() => handleCook(row)}
                className="mcm-focusable"
                style={{
                  display: "flex", alignItems: "stretch", gap: 12, width: "100%",
                  padding: "14px 14px 12px", background: "transparent",
                  border: "none", cursor: "pointer", textAlign: "left",
                  touchAction: "manipulation",
                }}
              >
                {/* Rank */}
                <div style={{
                  display: "flex", flexDirection: "column", alignItems: "center",
                  justifyContent: "center", width: 30, flex: "0 0 auto",
                }}>
                  <div style={{
                    fontFamily: FONT.mono, fontSize: 22, fontWeight: 800,
                    color: top3 ? COLOR.gold : INK_FAINT,
                    fontVariantNumeric: "tabular-nums", lineHeight: 1,
                  }}>
                    {rank}
                  </div>
                  {top3 && (
                    <div style={{
                      fontFamily: FONT.mono, fontSize: 12, fontWeight: 700,
                      color: "rgba(212,166,55,0.7)", letterSpacing: "0.06em", marginTop: 2,
                    }}>★</div>
                  )}
                </div>

                {/* Hero / emoji */}
                {row.goal_image_url ? (
                  <img src={row.goal_image_url} alt="" style={{
                    width: 56, height: 56, borderRadius: 12, objectFit: "cover",
                    background: "rgba(255,255,255,0.06)", flex: "0 0 auto",
                  }} />
                ) : (
                  <div style={{
                    width: 56, height: 56, borderRadius: 12, flex: "0 0 auto",
                    background: "rgba(255,255,255,0.06)",
                    display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28,
                  }}>
                    {row.emoji || "🍽️"}
                  </div>
                )}

                {/* Title + author + stats */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontFamily: FONT.itemTitle, fontStyle: "italic",
                    fontSize: 16, fontWeight: 500, color: INK, lineHeight: 1.2,
                    display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}>
                    {row.title}
                  </div>

                  {/* Author */}
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 6 }}>
                    <div style={{
                      width: 18, height: 18, borderRadius: 9, flexShrink: 0, overflow: "hidden",
                      background: "rgba(212,166,55,0.18)", border: "1px solid rgba(212,166,55,0.3)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                      {avatar
                        ? <img src={avatar} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                        : <span style={{ fontFamily: FONT.mono, fontSize: 12, fontWeight: 700, color: COLOR.gold }}>{author.charAt(0).toUpperCase()}</span>}
                    </div>
                    <span style={{ fontFamily: FONT.sans, fontSize: 12, color: INK_DIM, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      by {author}
                    </span>
                  </div>

                  {/* Stats line — stars (quality) + how many DIFFERENT
                      people cooked it (reach), the two rank drivers. */}
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 7, flexWrap: "wrap" }}>
                    {cooks > 0 ? (
                      <>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                          <Stars avg={avg} />
                          <span style={{ fontFamily: FONT.mono, fontSize: 12, fontWeight: 700, color: INK }}>
                            {((avg / 3) * 5).toFixed(1)}
                          </span>
                        </span>
                        <span style={{ fontFamily: FONT.mono, fontSize: 12, color: INK_DIM, letterSpacing: "0.03em" }}>
                          {cookers} {cookers === 1 ? "person" : "people"} · {cooks} cook{cooks === 1 ? "" : "s"}
                        </span>
                      </>
                    ) : (
                      <span style={{
                        fontFamily: FONT.mono, fontSize: 12, fontWeight: 700, color: COLOR.gold,
                        background: "rgba(212,166,55,0.12)", borderRadius: 6, padding: "1px 8px",
                        letterSpacing: "0.04em",
                      }}>
                        Not cooked yet — be the first
                      </span>
                    )}
                  </div>
                </div>

                {/* Cook affordance */}
                <div style={{
                  alignSelf: "center", flex: "0 0 auto",
                  fontFamily: FONT.mono, fontSize: 12, fontWeight: 700,
                  color: COLOR.gold, letterSpacing: "0.06em",
                  border: `1px solid ${GOLD_HAIR}`, borderRadius: 999,
                  padding: "7px 12px",
                }}>
                  COOK
                </div>
              </button>

              {/* Why this rank — plain language + the numbers, legible */}
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setOpenDiagId(isDiagOpen ? null : row.recipe_id); }}
                style={{
                  display: "block", width: "100%", padding: "9px 14px",
                  background: "transparent", border: "none", borderTop: `1px solid ${HAIR}`,
                  fontFamily: FONT.mono, fontSize: 12, color: INK_FAINT,
                  letterSpacing: "0.06em", textTransform: "uppercase", textAlign: "left",
                  cursor: "pointer", touchAction: "manipulation",
                }}
              >
                {isDiagOpen ? "− why this rank" : "+ why this rank"}
              </button>
              {isDiagOpen && (
                <div style={{
                  padding: "0 14px 14px",
                  fontFamily: FONT.sans, fontSize: 12, color: INK_DIM, lineHeight: 1.6,
                }}>
                  <p style={{ margin: "2px 0 10px" }}>
                    {cooks > 0
                      ? <>Cooked by <b style={{ color: INK }}>{allCookers} {allCookers === 1 ? "person" : "people"}</b> ({allTime} cook{allTime === 1 ? "" : "s"} total), averaging <b style={{ color: INK }}>{((avg / 3) * 5).toFixed(1)} of 5</b> stars{ratingWord(avg) ? <> — a {ratingWord(avg).toLowerCase()}</> : null}. Rank = rating quality × how many <b style={{ color: INK }}>different people</b> cooked it, so a recipe lots of people love beats a lone five-star, and a hugely-cooked dish tops a one-off. Recent cooks count more in Weekly and Monthly.</>
                      : <>No cooks yet, so brand-new recipes tie at the bottom, ordered newest-shared first. The moment someone cooks and rates it, it starts climbing. {sinceLabel(row.community_published_at)}.</>}
                  </p>
                  <div style={{
                    display: "grid", gridTemplateColumns: "auto 1fr", gap: "3px 12px",
                    fontFamily: FONT.mono, fontSize: 12, color: INK_FAINT,
                    borderTop: `1px solid ${HAIR}`, paddingTop: 8,
                  }}>
                    <span>Stars</span><span style={{ color: INK_DIM }}>{((avg / 3) * 5).toFixed(1)} / 5 ({avg.toFixed(2)}/3)</span>
                    <span>People</span><span style={{ color: INK_DIM }}>{allCookers} cooked it · {row.distinct_cookers_monthly} this month · {row.distinct_cookers_weekly} this week</span>
                    <span>Cooks</span><span style={{ color: INK_DIM }}>{allTime} total</span>
                    <span>Score</span><span style={{ color: INK_DIM }}>{Number(row[`score_${window}`] || 0).toFixed(3)} ({window})</span>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {rows.length > 0 && (
        <button
          type="button"
          onClick={() => refresh()}
          className="mcm-focusable"
          style={{
            display: "block", margin: "22px auto 0", padding: "8px 18px",
            background: "transparent", border: `1px solid ${HAIR}`,
            borderRadius: RADIUS.pill, fontFamily: FONT.mono, fontSize: 12,
            color: INK_FAINT, letterSpacing: "0.08em", textTransform: "uppercase",
            cursor: "pointer", touchAction: "manipulation",
          }}
        >
          refresh
        </button>
      )}
    </div>
  );
}
